import { LIGHT_BLUE, MEDIUM_BLUE, DARK_BLUE } from 'constants/colors';

import formatHeatmapDateLabel from './formatHeatmapDateLabel';

export interface HeatmapMatrix {
  dates: string[];
  rows: string[];
  cells: (number | null)[][];
  min: number;
  max: number;
  colorStops: string[];
  title?: string;
  subtitle?: string;
}

type AnyOption = Record<string, unknown>;

const asRecord = (v: unknown): AnyOption | undefined => (
  v && typeof v === 'object' ? (v as AnyOption) : undefined
);

function heatmapSeries(opt: AnyOption): AnyOption | undefined {
  const { series } = opt;
  const target = Array.isArray(series)
    ? series.find((s) => asRecord(s)?.type === 'heatmap')
    : series;
  const record = asRecord(target);
  return record?.type === 'heatmap' ? record : undefined;
}

function axisCategoryData(axis: unknown): string[] {
  const single = Array.isArray(axis) ? asRecord(axis[0]) : asRecord(axis);
  const data = single?.data;
  return Array.isArray(data) ? data.map(String) : [];
}

const DEFAULT_COLOR_STOPS = [LIGHT_BLUE, MEDIUM_BLUE, DARK_BLUE];

function colorStopsFromVisualMap(visualMap: AnyOption | undefined): string[] {
  const color = asRecord(visualMap?.inRange)?.color;
  return Array.isArray(color) && color.length > 0 && color.every((c) => typeof c === 'string')
    ? (color as string[])
    : DEFAULT_COLOR_STOPS;
}

function textField(record: AnyOption | undefined, key: string): string | undefined {
  const value = record?.[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function minFromVisualMapOrValues(visualMap: AnyOption | undefined, values: number[]): number {
  if (typeof visualMap?.min === 'number') {
    return visualMap.min;
  }
  return values.length ? Math.min(...values) : 0;
}

function maxFromVisualMapOrValues(visualMap: AnyOption | undefined, values: number[]): number {
  if (typeof visualMap?.max === 'number') {
    return visualMap.max;
  }
  return values.length ? Math.max(...values) : 1;
}

export default function heatmapMatrixFromOption(
  option: object | undefined,
): HeatmapMatrix | undefined {
  const opt = asRecord(option);
  if (!opt) return undefined;
  const series = heatmapSeries(opt);
  if (!series) return undefined;

  const dates = axisCategoryData(opt.xAxis).map(formatHeatmapDateLabel);
  const rows = axisCategoryData(opt.yAxis);
  if (dates.length === 0 || rows.length === 0) return undefined;

  const cells: (number | null)[][] = rows.map(() => dates.map(() => null));
  const points = Array.isArray(series.data) ? series.data : [];
  points.forEach((point) => {
    if (!Array.isArray(point) || point.length < 3) return;
    const [x, y, raw] = point;
    const col = Number(x);
    const row = Number(y);
    if (!Number.isInteger(col) || col < 0 || col >= dates.length) return;
    if (!Number.isInteger(row) || row < 0 || row >= rows.length) return;
    if (raw === null || raw === undefined || raw === '') return;
    const num = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(num)) cells[row][col] = num;
  });

  const vmRaw = opt.visualMap;
  const visualMap = asRecord(Array.isArray(vmRaw) ? vmRaw[0] : vmRaw);
  const values = cells.flat().filter((v): v is number => v !== null);
  const min = minFromVisualMapOrValues(visualMap, values);
  const max = maxFromVisualMapOrValues(visualMap, values);

  const titleRaw = opt.title;
  const title = asRecord(Array.isArray(titleRaw) ? titleRaw[0] : titleRaw);

  return {
    dates,
    rows,
    cells,
    min,
    max,
    colorStops: colorStopsFromVisualMap(visualMap),
    title: textField(title, 'text'),
    subtitle: textField(title, 'subtext'),
  };
}
